import { twMerge } from 'tailwind-merge';
import Flex from '../flex/Flex';
import { Icon, IconName } from '~/components/core/Icon';
import { colors } from '~/utils/tailwindUtils';
import React from 'react';

export type RadioOption = {
  id: string;
  label: string;
  value: string;
  iconName?: IconName;
};

export type RadioGroupProps = {
  value: string;
  options?: RadioOption[];
  onChange: (value: string) => void;
  iconName?: IconName;
  iconColor?: string;
  iconSize?: number;
  iconStrokeWidth?: number;
  children?:
    | ((props: { option: RadioOption }) => React.ReactNode)
    | React.ReactElement<{ option: RadioOption }>
    | React.ReactElement<{ option: RadioOption }>[];
  className?: string;
  labelClassName?: string;
  labelContainerClassName?: string;
};

const RadioGroup = ({
  value,
  options = [],
  onChange,
  iconColor,
  iconSize,
  iconStrokeWidth,
  children,
  className,
  labelClassName,
  labelContainerClassName,
}: RadioGroupProps) => {
  const handleChange = (selectedValue: string) => {
    onChange(selectedValue);
  };

  return (
    <Flex className={twMerge('gap-4', className)}>
      {options.map((option, index) => (
        <Flex
          key={option.id ?? `${option.value}-${index}`}
          className={twMerge('items-center cursor-pointer', labelContainerClassName)}
          onClick={() => handleChange(option.value.toString())}
        >
          <Flex className="items-center gap-3">
            <Icon
              name={
                option.value.toString() === value.toString()
                  ? IconName.RadioButtonChecked
                  : IconName.RadioButtonUnchecked
              }
              color={
                option.value.toString() === value.toString() ? colors.secondary : colors.primary
              }
              size={iconSize}
              customStrokeWidth={iconStrokeWidth ? iconStrokeWidth : 1}
            />
            {option.iconName && (
              <Icon
                name={option.iconName}
                color={iconColor}
                size={iconSize}
                customStrokeWidth={iconStrokeWidth ? iconStrokeWidth : 1.75}
              />
            )}
            <label
              className={twMerge(
                'text-sm font-medium leading-6 text-gray-900 cursor-pointer',
                labelClassName,
              )}
            >
              {option.label}
            </label>
          </Flex>
          {/* {children} */}
          {/* Inject option into children */}
          {children &&
            (typeof children === 'function'
              ? children({ option })
              : React.Children.map(children, (child) => {
                  if (React.isValidElement<{ option: any }>(child)) {
                    return React.cloneElement(child, { option });
                  }
                  return child;
                }))}
        </Flex>
      ))}
    </Flex>
  );
};
export default RadioGroup;
